import { type ReactNode } from 'react'
import { styled } from '@linaria/react'
import { minimalVars, from, until } from '@ovasabi/ui-minimal/tokens'
import { MinimalSkipLink } from '@ovasabi/ui-minimal'
import type { DockTab } from '../../lib/edition'
import { AppDock } from './AppDock'
import { AppTopNav } from './AppTopNav'
import { desktopGutter, paddedMaxWidth } from './layout'

const Style = {
  /* Exactly one viewport tall: the top nav (desktop) takes its row and the
     frame gets the rest. html/body/#root clip at the viewport, so a frame that
     was itself 100dvh sat under the nav with its bottom 61px unreachable. */
  Stage: styled.div`
    height: 100dvh;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    background: ${minimalVars.color.bgApp};
    box-sizing: border-box;
  `,
  Frame: styled.div`
    position: relative;
    width: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${minimalVars.color.bgApp};

    width: 100%;
    max-width: 100%;
    margin: 0;

    ${from('page')} {
      max-width: 100%;
      border-radius: 0;
      border: none;
      box-shadow: none;
    }
  `,
  Scroll: styled.main`
    flex: 1;
    min-width: 0;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
  `,
  Body: styled.div`
    display: grid;
    /* Single column capped at the frame width on mobile, expands on desktop */
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
    padding: 10px 14px 44px;
    width: 100%;
    box-sizing: border-box;
    min-width: 0;
    > * { min-width: 0; }

    &[data-fill='true'] {
      flex: 1;
      min-height: 0;
      grid-template-rows: minmax(0, 1fr);
      padding-bottom: 14px;
    }

    ${until('page')} {
      &[data-flush-mobile='true'] {
        padding: 0;
        gap: 0;
      }
    }

    /* Desktop keeps the wider gutters but not the airy rhythm: spacing.xl
       resolves to 48px at this width, which put a gap the height of a card
       between a filter row and the list it filters. md (20px) holds the page
       together as one column — sections still read as separate without a band
       of empty ground between them — and the bottom pad only has to clear the
       end of the scroll, since the dock is hidden above 768px. */
    /* Same content column as the top nav and the page header (see layout.ts),
       so all three share left and right edges.

       The top pad is not optional: the header is sticky, rounded, and casts a
       shadow, so without it the first row of cards sits flush against the
       header's bottom edge and reads as tucked under it. The bottom keeps 32px
       purely as scroll clearance — without it the last row ends flush against
       the bottom of the viewport with no indication the list has finished. */
    ${from('page')} {
      max-width: ${paddedMaxWidth};
      margin: 0 auto;
      padding: 16px ${desktopGutter}px 40px;
      gap: ${minimalVars.space.sm};
    }
  `,
  DockWrap: styled.div`
    ${from('page')} {
      display: none;
    }
  `,
}

/**
 * Responsive application shell.
 *
 * - On mobile (<768px): Compact phone column with bottom dock.
 * - On desktop (≥768px): Opens into a full-featured desktop kitchen application
 *   with sticky top navigation bar, multi-column expanded workspace, and desktop
 *   table border ergonomics.
 */
export const MobileShell = ({
  children,
  badges,
  floatingAction,
  showCart = true,
  tabs,
  cartCount = 0,
}: {
  children: ReactNode
  badges?: Record<string, number>
  floatingAction?: ReactNode
  showCart?: boolean
  tabs?: readonly DockTab[]
  cartCount?: number
}) => (
  <Style.Stage>
    <MinimalSkipLink />
    <AppTopNav badges={badges} tabs={tabs} cartCount={cartCount} mobileActions={floatingAction} showCart={showCart} />
    <Style.Frame>
      {/* The scroll container; PageTransition resets it to top when a new route
          mounts. */}
      <Style.Scroll id="main-content" tabIndex={-1}>
        {children}
      </Style.Scroll>
      <Style.DockWrap>
        <AppDock badges={badges} tabs={tabs} />
      </Style.DockWrap>
    </Style.Frame>
  </Style.Stage>
)

/** Page scaffold: a full-bleed `header` (PageHeader) over a padded body. */
export const Screen = ({
  header,
  children,
  fill = false,
  flushMobile = false,
}: {
  header: ReactNode
  children: ReactNode
  fill?: boolean
  flushMobile?: boolean
}) => (
  <>
    {header}
    <Style.Body data-fill={fill} data-flush-mobile={flushMobile}>{children}</Style.Body>
  </>
)
